/**
 * @file resolveDesktopPinClaim.ts
 * @description Turns the holders of one pin path into what the claim banner
 * renders. Consumer: `DesktopKeepBannerHolders`, which supplies the signed-in
 * uid so the reader is never listed among "also".
 *
 * The copy states no total and no recency. `desktopPinPaths` is a derived,
 * last-writer-wins projection that can under-report, so a printed count would
 * assert an exhaustiveness the data cannot support; the sentence therefore names
 * the people it knows and says "and more" past three, and never a figure. Only
 * `meta.created` exists — there is no last-synced time per pin — so the date is
 * introduced by "since" and carries its year, which lets a reader looking at a
 * March date draw the conclusion themselves rather than be told a freshness
 * nobody measured.
 *
 * Holders arrive oldest pin first, so `holders[0]` is the one the sentence dates.
 * A holder keeping zero files is indistinguishable here and is listed like any
 * other: `DesktopPinHolder` carries no scope, and inferring one would be a guess.
 */

import { Timestamp } from 'firebase/firestore';

import type { TimeType } from '#types';
import type { DesktopPinHolder } from '@/use/desktopSync/useDesktopPinHolders';

export type DesktopPinClaimAvatar = {
    userId: string;
    /** Up to two letters. `User` carries no photo field, so this is the whole avatar. */
    initials: string;
    /** Tooltip for one avatar: who they are and when their pin was created. */
    title: string;
};

export type DesktopPinClaim = {
    avatars: readonly DesktopPinClaimAvatar[];
    sentence: string;
};

const displayName = (holder: DesktopPinHolder): string =>
    holder.name.trim() || holder.email.trim() || 'Unknown user';

const initialsOf = (holder: DesktopPinHolder): string => {
    const words = displayName(holder).split(/\s+/).filter(Boolean);
    const letters = words.slice(0, 2).map((word) => word[0]).join('');
    return letters.toUpperCase() || '?';
};

const formatSince = (since: TimeType | undefined): string | null =>
    since instanceof Timestamp
        ? since.toDate().toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
        : null;

const listNames = (names: readonly string[]): string => {
    if (names.length === 1) return names[0];
    if (names.length === 2) return `${names[0]} and ${names[1]}`;
    if (names.length === 3) return `${names[0]}, ${names[1]} and ${names[2]}`;
    return `${names[0]}, ${names[1]}, ${names[2]} and more`;
};

export const resolveDesktopPinClaim = (
    holders: readonly DesktopPinHolder[],
    viewerUserId: string | undefined,
    artifactLabel: 'form' | 'record',
): DesktopPinClaim | null => {
    const others = holders.filter((holder) => holder.userId !== viewerUserId);
    if (others.length === 0) {
        return null;
    }

    const avatars = others.map((holder) => {
        const since = formatSince(holder.since);
        return {
            userId: holder.userId,
            initials: initialsOf(holder),
            title: since ? `${displayName(holder)} — since ${since}` : displayName(holder),
        };
    });

    const names = others.map(displayName);
    const oldest = others[0];
    const oldestSince = formatSince(oldest.since);

    if (others.length === 1) {
        return {
            avatars,
            sentence: oldestSince
                ? `Also kept by ${names[0]}, since ${oldestSince}.`
                : `Also kept by ${names[0]}.`,
        };
    }

    return {
        avatars,
        sentence: oldestSince
            ? `Also kept by ${listNames(names)}. ${displayName(oldest)} has kept this ${artifactLabel} since ${oldestSince}.`
            : `Also kept by ${listNames(names)}.`,
    };
};
